/* eslint-disable react-refresh/only-export-components */
import { createContext, useEffect, useState } from 'react';
import { GetPublicCompanyQuery } from '../../gql/graphql.ts';
import { PublicCompanyQuery } from '../queries/PublicQueries';
import { useQuery } from 'urql';
import { cloneAndRemoveTypename } from '../util.ts';
import { useKeycloak } from '../../shared/auth/KeycloakProvider.tsx';

export type PublicCompanyData = GetPublicCompanyQuery['publicCompany'];

export type PublicCompanyContextType = [
  company: PublicCompanyData | undefined,
  setCompany: React.Dispatch<React.SetStateAction<PublicCompanyData>>,
];

export const PublicCompanyContext = createContext<PublicCompanyContextType>([
  undefined,
  () => {},
]);

interface PublicCompanyProviderProps {
  children: React.ReactNode;
}

// This provider fetches and provides the data from the publicCompany GQL Query, which returns basic information for the currently requested company
// (e.g. all company settings needed to properly render the user-facing frontend)
export const PublicCompanyProvider: React.FC<PublicCompanyProviderProps> = ({
  children,
}) => {
  const [company, setCompany] = useState<PublicCompanyData>();
  const { login } = useKeycloak();

  const [publicCompanyQueryResult] = useQuery({
    query: PublicCompanyQuery,
  });

  const { publicCompany } = publicCompanyQueryResult.data || {};

  useEffect(() => {
    if (publicCompany) {
      // Cache deep-cloned (no __typename) copy so consumers can mutate freely.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCompany(cloneAndRemoveTypename(publicCompany));
    } else if (publicCompanyQueryResult.error) {
      void login().catch((err) => console.error('[Auth] Login failed:', err));
    }
  }, [login, publicCompany, publicCompanyQueryResult.error]);

  return (
    <PublicCompanyContext.Provider
      value={[
        company,
        setCompany as React.Dispatch<React.SetStateAction<PublicCompanyData>>,
      ]}
    >
      {children}
    </PublicCompanyContext.Provider>
  );
};
